// components/Outreach/ReplyIntentionsTab.js — обновлён: worker обязателен для всех
import React, { useState, useEffect } from "react";
import { getCookie } from "../../services/cookie";

const API_BASE = "https://flautreach.com/flautreach/api/outreach";

export function ReplyIntentionsTab({ add }) {
  const token = () => getCookie("authToken");
  const getHeaders = () => {
    const t = token();
    return { "Content-Type": "application/json", ...(t ? { Authorization: `Bearer ${t}` } : {}) };
  };

  // Тип контакта и поля
  const [contactType, setContactType] = useState("EMAIL");
  const [email, setEmail] = useState("");
  const [fbUrl, setFbUrl] = useState("");
  const [contactEmail, setContactEmail] = useState(""); // для CONTACT_FORM

  // Общие поля
  const [replyText, setReplyText] = useState("");
  const [intentions, setIntentions] = useState([]);
  const [creating, setCreating] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [executionsList, setExecutionsList] = useState([]);
  const [selectedExecId, setSelectedExecId] = useState("");
  const [searching, setSearching] = useState(false);

  // Воркеры и выбранный воркер (обязателен для всех)
  const [workers, setWorkers] = useState([]);
  const [selectedWorker, setSelectedWorker] = useState("");

  // Галочки для массового выполнения
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [executing, setExecuting] = useState(false);

  useEffect(() => { loadIntentions(); loadWorkers(); }, [page]);

  const loadIntentions = async () => {
    try {
      const resp = await fetch(`${API_BASE}/reply-intentions/?page=${page}&page_size=10`, { headers: getHeaders() });
      if (resp.ok) {
        const data = await resp.json();
        setIntentions(data.results || data || []);
        setTotalPages(data.pagination?.total_pages || 1);
      }
    } catch {}
  };

  const loadWorkers = async () => {
    try {
      const resp = await fetch(`${API_BASE}/workers/?page_size=100`, { headers: getHeaders() });
      if (resp.ok) {
        const data = await resp.json();
        const list = Array.isArray(data) ? data : data.results || [];
        setWorkers(list.filter(w => w.is_active));
      }
    } catch {}
  };

  // Сброс при смене типа контакта (воркера не сбрасываем)
  const resetForm = (newType) => {
    setContactType(newType);
    setExecutionsList([]);
    setSelectedExecId("");
  };

  // Поиск executions через FindExecutionByContactView
  const handleSearch = () => {
    setExecutionsList([]);
    setSelectedExecId("");

    let searchValue = "";
    if (contactType === "EMAIL") {
      if (!email.trim()) { add?.("Введи email", "error"); return; }
      searchValue = email.trim();
    } else if (contactType === "FB_MESSENGER") {
      if (!fbUrl.trim()) { add?.("Введи URL", "error"); return; }
      searchValue = fbUrl.trim();
    } else if (contactType === "CONTACT_FORM") {
      if (!contactEmail.trim()) { add?.("Введи email", "error"); return; }
      searchValue = contactEmail.trim();
    }
    if (!selectedWorker) { add?.("Выбери воркера", "error"); return; }

    setSearching(true);
    const params = new URLSearchParams({
      contact: searchValue,
      contact_type: contactType,
      worker_name: selectedWorker,
    });

    fetch(`${API_BASE}/executions/find_by_contact/?${params}`, { headers: getHeaders() })
      .then(resp => resp.json())
      .then(data => {
        const execs = data.executions || [];
        if (execs.length === 0) {
          add?.(data.error || "Диалоги не найдены", "error");
        } else {
          setExecutionsList(execs);
          setSelectedExecId(execs[0].execution_id);
        }
      })
      .catch(() => add?.("Ошибка поиска", "error"))
      .finally(() => setSearching(false));
  };

  // Автопоиск только когда контакт + воркер заполнены
  useEffect(() => {
    if (contactType === "EMAIL" && email.trim().includes("@") && selectedWorker) {
      const timer = setTimeout(handleSearch, 700);
      return () => clearTimeout(timer);
    }
  }, [email, selectedWorker, contactType]);

  useEffect(() => {
    if (contactType === "FB_MESSENGER" && fbUrl.trim().length > 5 && selectedWorker) {
      const timer = setTimeout(handleSearch, 700);
      return () => clearTimeout(timer);
    }
  }, [fbUrl, selectedWorker, contactType]);

  // Создание Reply Intention
  const createReplyIntention = async () => {
    if (!replyText.trim()) { add?.("Введи текст", "error"); return; }

    let contactValue = "";
    if (contactType === "EMAIL") {
      if (!email.trim()) { add?.("Введи email", "error"); return; }
      contactValue = email.trim();
    } else if (contactType === "FB_MESSENGER") {
      if (!fbUrl.trim()) { add?.("Введи URL", "error"); return; }
      contactValue = fbUrl.trim();
    } else if (contactType === "CONTACT_FORM") {
      if (!contactEmail.trim()) { add?.("Введи email", "error"); return; }
      contactValue = contactEmail.trim();
    }
    if (!selectedWorker) { add?.("Выбери воркера", "error"); return; }
    if (!selectedExecId) { add?.("Найди и выбери execution", "error"); return; }

    setCreating(true);
    try {
      await fetch(`${API_BASE}/reply-intentions/`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({
          execution_id: parseInt(selectedExecId),
          reply_text: replyText.trim(),
          contact_type: contactType,
          contact_value: contactValue,
        }),
      });
      add?.("✅ Reply Intention создан", "success");
      setReplyText("");
      setEmail("");
      setFbUrl("");
      setContactEmail("");
      setExecutionsList([]);
      setSelectedExecId("");
      loadIntentions();
    } catch (err) {
      add?.("Ошибка", "error");
    } finally {
      setCreating(false);
    }
  };

  // Массовое выполнение
  const executeChecked = async () => {
    if (checkedIds.size === 0) { add?.("Отметь галочки", "error"); return; }
    setExecuting(true);
    try {
      await fetch(`${API_BASE}/reply-intentions/execute_bulk/`, {
        method: "POST",
        headers: getHeaders(),
        body: JSON.stringify({ ids: [...checkedIds] }),
      });
      add?.(`Выполнено: ${checkedIds.size}`, "success");
      setCheckedIds(new Set());
      loadIntentions();
    } catch (err) {
      add?.("Ошибка", "error");
    } finally {
      setExecuting(false);
    }
  };

  const deleteIntention = async (id) => {
    if (!window.confirm(`Удалить интеншн #${id}?`)) return;
    try {
      await fetch(`${API_BASE}/reply-intentions/${id}/`, { method: "DELETE", headers: getHeaders() });
      add?.("Удалён", "info");
      loadIntentions();
    } catch (err) {
      add?.("Ошибка", "error");
    }
  };

  const toggleCheck = (id) => {
    setCheckedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const contactTypes = [
    { value: "EMAIL", label: "📧 Email" },
    { value: "FB_MESSENGER", label: "💬 FB Messenger" },
    { value: "CONTACT_FORM", label: "📝 Contact Form" },
  ];

  return (
    <div>
      <div className="section-card" style={{ marginBottom: 20 }}>
        <h3>📋 Создать Reply Intention</h3>

        {/* Тип контакта */}
        <div className="form-group">
          <label className="form-label">Тип контакта</label>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
            {contactTypes.map(ct => (
              <button
                key={ct.value}
                className={`channel-tab ${contactType === ct.value ? "active" : ""}`}
                onClick={() => resetForm(ct.value)}
              >
                {ct.label}
              </button>
            ))}
          </div>
        </div>

        {/* Выбор воркера (общий для всех) */}
        <div className="form-group">
          <label className="form-label">👷 Выбери воркера *</label>
          <select
            className="form-input"
            style={{ paddingLeft: 14 }}
            value={selectedWorker}
            onChange={e => {
              setSelectedWorker(e.target.value);
              setExecutionsList([]);
              setSelectedExecId("");
            }}
          >
            <option value="">-- Выбери воркера --</option>
            {workers.map(w => (
              <option key={w.id} value={w.name}>
                {w.name} (лимит: {w.daily_limit || "—"})
              </option>
            ))}
          </select>
        </div>

        {/* Поля контакта */}
        {contactType === "EMAIL" && (
          <div className="form-group">
            <label className="form-label">Email *</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                className="form-input"
                style={{ paddingLeft: 14, flex: 1 }}
                value={email}
                onChange={e => { setEmail(e.target.value); setExecutionsList([]); setSelectedExecId(""); }}
                placeholder="user@example.com"
              />
              <button className="btn-add" onClick={handleSearch} disabled={searching || !email.trim() || !selectedWorker}>
                {searching ? "⏳" : "🔍"} Найти
              </button>
            </div>
          </div>
        )}

        {contactType === "FB_MESSENGER" && (
          <div className="form-group">
            <label className="form-label">URL Facebook *</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                className="form-input"
                style={{ paddingLeft: 14, flex: 1 }}
                value={fbUrl}
                onChange={e => { setFbUrl(e.target.value); setExecutionsList([]); setSelectedExecId(""); }}
                placeholder="https://www.facebook.com/..."
              />
              <button className="btn-add" onClick={handleSearch} disabled={searching || !fbUrl.trim() || !selectedWorker}>
                {searching ? "⏳" : "🔍"} Найти
              </button>
            </div>
          </div>
        )}

        {contactType === "CONTACT_FORM" && (
          <div className="form-group">
            <label className="form-label">Email отправителя *</label>
            <div style={{ display: "flex", gap: 8 }}>
              <input
                className="form-input"
                style={{ paddingLeft: 14, flex: 1 }}
                value={contactEmail}
                onChange={e => { setContactEmail(e.target.value); setExecutionsList([]); setSelectedExecId(""); }}
                placeholder="user@example.com"
              />
              <button className="btn-add" onClick={handleSearch} disabled={searching || !contactEmail.trim() || !selectedWorker}>
                {searching ? "⏳" : "🔍"} Найти
              </button>
            </div>
          </div>
        )}

        {/* Результаты поиска */}
        {executionsList.length > 0 && (
          <div className="form-group">
            <label className="form-label">
              {executionsList.length === 1 ? "✅ Диалог" : `Найдено ${executionsList.length} диалогов`}
            </label>
            <select
              className="form-input"
              style={{ paddingLeft: 14 }}
              value={selectedExecId}
              onChange={e => setSelectedExecId(e.target.value)}
            >
              {executionsList.map(ex => (
                <option key={ex.execution_id} value={ex.execution_id}>
                  #{ex.execution_id} · 🤖 {ex.worker_name} · {ex.campaign_name} · {ex.target_name || ex.target_url?.substring(0, 30)} · {new Date(ex.exec_created).toLocaleString()}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Текст ответа */}
        <div className="form-group">
          <label className="form-label">Текст ответа *</label>
          <textarea
            className="intent-textarea"
            style={{ minHeight: 80 }}
            value={replyText}
            onChange={e => setReplyText(e.target.value)}
            placeholder="Текст который нужно отправить в ответ..."
          />
        </div>

        <button
          className="btn-primary"
          style={{ width: "auto", padding: "13px 32px" }}
          disabled={creating || !selectedExecId || !replyText.trim()}
          onClick={createReplyIntention}
        >
          {creating ? "⏳" : "→"} Создать Reply Intention
        </button>
      </div>

      {/* Список существующих интеншенов */}
      <div className="section-card">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
          <h3>Интеншены ({checkedIds.size} отмечено)</h3>
          <button className="btn-add" style={{ padding: "8px 16px", fontSize: 12 }} disabled={executing} onClick={executeChecked}>
            {executing ? "⏳" : "✓"} Выполнить отмеченные
          </button>
        </div>

        <div className="rules-grid">
          {intentions.length === 0 ? (
            <div className="empty-state" style={{ padding: 20 }}><p>Нет интеншенов</p></div>
          ) : (
            intentions.map(inv => (
              <div key={inv.id} className="rule-card" style={{ padding: "14px 18px", opacity: inv.status === 'DONE' ? 0.6 : 1 }}>
                <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
                  {inv.status === 'PENDING' && (
                    <input type="checkbox" checked={checkedIds.has(inv.id)} onChange={() => toggleCheck(inv.id)}
                      style={{ marginTop: 3, width: 18, height: 18, cursor: "pointer", flexShrink: 0 }} />
                  )}
                  {inv.status === 'DONE' && <span style={{ color: "#16a34a", fontWeight: 700, flexShrink: 0 }}>✓</span>}
                  <div style={{ flex: 1 }}>
                    <strong>#{inv.id}</strong>
                    <span style={{ marginLeft: 10, fontSize: 12, color: "var(--muted)" }}>
                      {inv.contact_type && `${inv.contact_type === "EMAIL" ? "📧" : inv.contact_type === "FB_MESSENGER" ? "💬" : "📝"} `}
                      {inv.email || inv.fb_url || inv.contact_value || "—"} · Exec #{inv.execution_id}
                    </span>
                    <p style={{ fontSize: 12, marginTop: 4 }}>{(inv.reply_text || "").substring(0, 100)}</p>
                  </div>
                  <button className="btn-delete" onClick={() => deleteIntention(inv.id)}>🗑</button>
                </div>
              </div>
            ))
          )}
        </div>
        {totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "center", gap: 8, marginTop: 16 }}>
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>←</button>
            <span style={{ fontSize: 13, color: "var(--muted)" }}>{page} / {totalPages}</span>
            <button disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>→</button>
          </div>
        )}
      </div>
    </div>
  );
}